import {inject, Injectable, OnDestroy} from "@angular/core";
import {BehaviorSubject, Observable, tap} from "rxjs";
import {
  AllowanceEntity,
  IncomeTaxEntity,
  MenuInterface,
  Toeslagen,
  ToeslagenBerekening, VpbBerekening
} from './api.bokkz.services';
import {ErrorMessage, TslRekenService} from './rekenclient.api';

@Injectable({providedIn: 'root'})
export class BokkzTslDataService implements OnDestroy {

  private apiService = inject(TslRekenService);

  public allowanceEntity = new BehaviorSubject<AllowanceEntity>(this.getFromSessionStorage<AllowanceEntity>('allowanceEntity') || {} as AllowanceEntity);
  public incomeTaxEntity = new BehaviorSubject<IncomeTaxEntity>(this.getFromSessionStorage<IncomeTaxEntity>('incomeTaxEntity') || {} as IncomeTaxEntity);
  public tslBerekening = new BehaviorSubject<ToeslagenBerekening>(this.getFromSessionStorage<ToeslagenBerekening>('tslBerekening') || {} as ToeslagenBerekening);
  public toetsingsInkomen = new BehaviorSubject<number>(0);
  public menuInterface = new BehaviorSubject<MenuInterface[]>([]);
  public errorResponse = new BehaviorSubject<ErrorMessage[]>([]);
  public aantalValidaties = new BehaviorSubject<number>(0);
  public hasKinderen = new BehaviorSubject<boolean>(false);
  public hasPartner = new BehaviorSubject<boolean>(false);
  public hasSstIb = new BehaviorSubject<boolean>(false);
  public hasHuurwoningen = new BehaviorSubject<boolean>(false);

  public allowanceEntity$: Observable<AllowanceEntity> = this.allowanceEntity.asObservable();
  public tslBerekening$: Observable<ToeslagenBerekening> = this.tslBerekening.asObservable();
  public aantalValidaties$: Observable<number> = this.aantalValidaties.asObservable();
  public errorResponse$: Observable<ErrorMessage[]> = this.errorResponse.asObservable();
  public menuInterface$: Observable<MenuInterface[]> = this.menuInterface.asObservable();

  public patchAllowanceEntity(updater: (entity: AllowanceEntity) => AllowanceEntity): void {
    const current = this.allowanceEntity.value;
    this.allowanceEntity.next(updater(current));
  }

  calculateLatestAllowances(): Observable<ToeslagenBerekening> {
    const aangifte = this.allowanceEntity.value.toeslagen;

    if (aangifte && aangifte.geboortedatumAanvrager !== undefined && aangifte.berekeningsjaar !== undefined) {
      return this.apiService.getToeslagenCalculation(aangifte).pipe(
        tap(result => {
          this.tslBerekening.next(result);
        })
      );
    } else {
      this.tslBerekening.next({} as ToeslagenBerekening);
      return new Observable();
    }
  }

  constructor() {
    this.applyDebounceTime();
  }

  private applyDebounceTime() {
    this.allowanceEntity.subscribe(value => this.saveToSessionStorage('allowanceEntity', value));
    this.incomeTaxEntity.subscribe(value => this.saveToSessionStorage('incomeTaxEntity', value));
    this.tslBerekening.subscribe(value => this.saveToSessionStorage('tslBerekening', value));
  }

  private getFromSessionStorage<T>(key: string): T | null {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  }

  private saveToSessionStorage(key: string, value: any) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  public updateEntity(entity: AllowanceEntity) {
    this.allowanceEntity.next(entity)
  }

  public updateToeslagen(toeslagen: Toeslagen) {

    if (toeslagen.inkomensgegevens?.at(0)?.toeslagPartner) {
      this.hasPartner.next(true);
    } else if (!toeslagen.inkomensgegevens?.at(0)?.toeslagPartner) {
      this.hasPartner.next(false);
    }

      if (toeslagen.bsn != null) {
          toeslagen.bsn = toeslagen.bsn.padStart(9, '0');
      }

      if (Array.isArray(toeslagen.inkomensgegevens)) {
      toeslagen.inkomensgegevens.forEach(inkomenGegevens => {
        if (!inkomenGegevens.toeslagPartner) {
          inkomenGegevens.geboortedatumPartner = undefined;
          inkomenGegevens.bsnPartner = undefined;
          inkomenGegevens.toetsinkomenPartner = undefined;
          inkomenGegevens.toetsvermogenPartner = undefined;
          inkomenGegevens.partnerHeeftZorgverzekering = undefined

        }
      });
    }

    if (toeslagen.kinderen !== undefined && toeslagen.kinderen.length > 0) {
      this.hasKinderen.next(true);
    } else {
      this.hasKinderen.next(false);
    }

    if (toeslagen.huurtoeslag !== undefined && toeslagen.huurtoeslag.huurwoningen !== undefined && toeslagen.huurtoeslag.huurwoningen.length > 0) {
      this.hasHuurwoningen.next(true);
    } else {
      this.hasHuurwoningen.next(false);
    }
    return toeslagen;
  }

  public updateAllowanceTaxEntity(allowanceEntity: AllowanceEntity) {
    this.allowanceEntity.next(allowanceEntity)
  }

  public updateToeslagenBerekening(toeslagenBerekening: ToeslagenBerekening) {
    this.tslBerekening.next(toeslagenBerekening)
  }

  public updateErrorResponse(errorResponse: ErrorMessage[]) {
    this.errorResponse.next(errorResponse);
  }

  public updateAantalValidaties(aantalValidaties: number) {
    this.aantalValidaties.next(aantalValidaties)
  }

  public createMenuInterface() {
    const menuInterface : MenuInterface[] = [
      {
        value: 0,
        id: '0',
        label: 'Gegevens & zorgtoeslag',
        visible: true,
        subonderdelen: [
          {
            label: 'Aanvrager',
            pageMapping: 'Aanvrager',
            visible: true,
            value: 11
          },
          {
            label: 'Partner',
            pageMapping: 'Partner',
            visible: true,
            value: 12
          }
        ],
      },
      {
        value: 1,
        id: '1',
        label: 'Kindgebonden budget',
        visible: true,
        subonderdelen: [
          {
            label: 'Kinderen',
            pageMapping: 'Kinderen',
            visible: true,
            value: 21
          }
        ],
      },
      {
        value: 2,
        id: '2',
        label: 'Kinderopvangtoeslag',
        visible: true,
        subonderdelen: [
          {
            label: 'Kinderopvang',
            pageMapping: 'Kinderopvang',
            visible: true,
            value: 31
          }
        ],
      },
      {
        value: 3,
        id: '3',
        label: 'Huurtoeslag',
        visible: true,
        subonderdelen: [
          {
            label: 'Huurwoning',
            pageMapping: 'Huurwoning',
            visible: true,
            value: 41
          }
        ],
      },
      {
        value: 4,
        id: '4',
        label: 'Overzicht',
        visible: true,
        subonderdelen: [
          {
            label: 'Voorschot',
            pageMapping: 'Voorschot',
            visible: true,
            value: 51
          },
          {
            label: 'Berekening',
            pageMapping: 'Berekening',
            visible: true,
            value: 52
          }
        ],
      },
    ];
    this.menuInterface.next(menuInterface);
  }

  ngOnDestroy(): void {
    if (this.hasPartner){
      this.hasPartner?.unsubscribe()
    }
    if (this.hasKinderen){
      this.hasKinderen?.unsubscribe()
    }
    if (this.hasHuurwoningen){
      this.hasHuurwoningen?.unsubscribe()
    }
    if (this.hasSstIb){
      this.hasSstIb?.unsubscribe()
    }
    if (this.errorResponse){
      this.errorResponse?.unsubscribe()
    }
    if (this.aantalValidaties){
      this.aantalValidaties?.unsubscribe()
    }
    if (this.allowanceEntity){
      this.allowanceEntity.unsubscribe()
    }
    if (this.tslBerekening){
      this.tslBerekening?.unsubscribe()
    }
    if (this.toetsingsInkomen){
      this.toetsingsInkomen?.unsubscribe()
    }
    if (this.menuInterface){
      this.menuInterface?.unsubscribe()
    }
  }
}
